import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  actionSuggestionCalendarDayKey,
  type ActionSuggestionKind,
} from "./action-suggestion";

export type DismissedKindsByMentorship = Readonly<
  Record<string, readonly ActionSuggestionKind[]>
>;

interface ActionSuggestionDismissals {
  dayKey: string;
  byMentorship: DismissedKindsByMentorship;
  dismiss: (
    mentorshipId: string,
    kind: ActionSuggestionKind,
    dayKey?: string,
  ) => void;
}

const EMPTY: DismissedKindsByMentorship = {};

// Dismissals live for one America/New_York calendar day; a new day drops the map.
export const useActionSuggestionDismissals = create(
  persist<ActionSuggestionDismissals>(
    (set, get) => ({
      dayKey: "",
      byMentorship: EMPTY,
      dismiss: (
        mentorshipId,
        kind,
        dayKey = actionSuggestionCalendarDayKey(),
      ) => {
        if (!mentorshipId || kind === "empty") return;
        const current = selectDismissedKindsByMentorship(get(), dayKey);
        const kinds = current[mentorshipId] ?? [];
        if (kinds.includes(kind)) return;
        set({
          dayKey,
          byMentorship: { ...current, [mentorshipId]: [...kinds, kind] },
        });
      },
    }),
    { name: "action-suggestion-dismissals" },
  ),
);

export function selectDismissedKindsByMentorship(
  state: Pick<ActionSuggestionDismissals, "dayKey" | "byMentorship">,
  dayKey: string = actionSuggestionCalendarDayKey(),
): DismissedKindsByMentorship {
  return state.dayKey === dayKey ? state.byMentorship : EMPTY;
}

export function useDismissedKindsByMentorship(): DismissedKindsByMentorship {
  return useActionSuggestionDismissals((state) =>
    selectDismissedKindsByMentorship(state),
  );
}
